import { Directive, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { FormControl, NG_VALIDATORS } from '@angular/forms';
import { isValid } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';

import { KEY_CODE } from '../key-codes';

// import * as datepicker from 'air-datepicker';
declare let $: any;

/**
 * Directive to create a date time picker on an input control.
 * Example:  <input type="input" [date]="myModel.date" (dateChanged)="myModel.date = $event">
 */
@Directive({
    selector: '[floDateTimePicker]',
    providers: [
        { provide: NG_VALIDATORS, useExisting: DateTimePickerDirective, multi: true },
    ],
})
export class DateTimePickerDirective {
    @Output() public dateChange: EventEmitter<Date> = new EventEmitter<Date>();
    @Input() public enableTimePicker: boolean = false;
    @Input() public onlyTimePicker: boolean = false;
    @Input() public minuteStep: number = 5;
    @Input() public dateFormat: string = 'mm/dd/yyyy';
    @Input() public timeFormat: string = 'hh:ii aa';
    @Input() public position: string = 'bottom left';

    public datePickerInitialized: boolean = false;
    public datepicker: any;
    public selectedDate: Date = null;
    public validDateRegex: string = '^[0-3]?[0-9].[0-3]?[0-9].(?:[0-9]{2})?[0-9]{2}$';

    private formControl: FormControl;
    private _timeZone: string;
    @Input()
    set timeZone(timeZone: string) {
        if (timeZone &&
        this.date &&
        this.timeZone &&
        this.timeZone !== timeZone) {
            const origTimeZone: string = this.timeZone;
            this._timeZone = timeZone;
            // Convert from the previously set time zone to the new timeZone, then back to local computer date for js Date object
            const utcTimeZone: Date = zonedTimeToUtc(this.date, origTimeZone);
            const date: Date = utcToZonedTime(utcTimeZone, timeZone);
            this.setDate(date, false);
        } else {
            this._timeZone = timeZone;
        }
    }

    get timeZone(): string {
        return this._timeZone;
    }

    private _date: Date;
    @Input()
    set date(date: Date) {
        this.setDate(date, true);
    }

    get date(): Date {
        return this._date;
    }

    /**
     * Constructor - injects the related element and it's NgModel
     */
    constructor(public el: ElementRef) {
        el.nativeElement.autocomplete = 'off';
    }

    @HostListener('keydown', ['$event'])
    public keydown($event: KeyboardEvent): any {
        const allowedKeys: number[] = [
            KEY_CODE.KEY_UP,
            KEY_CODE.KEY_DOWN,
            KEY_CODE.KEY_LEFT,
            KEY_CODE.KEY_RIGHT,
            KEY_CODE.KEY_SHIFT,
            KEY_CODE.KEY_ALT,
            KEY_CODE.KEY_CONTROL,
            KEY_CODE.KEY_HOME,
            KEY_CODE.KEY_END,
            KEY_CODE.KEY_BACK_SPACE,
            KEY_CODE.KEY_DELETE,
            KEY_CODE.KEY_TAB,
            KEY_CODE.KEY_ENTER,
            KEY_CODE.KEY_RETURN,
        ];

        // Make sure the user doesn't put in alpha or invalid characters
        if ($event.key &&
            $event.which &&
            allowedKeys.filter((key: number) => key === $event.which).length === 0) {
            const key: string = $event.key.toLowerCase();

            const disabledTimePickerKeys: string[] = ['/', '-', 'delete', 'backspace'];
            const enabledTimePickerKeys: string[] = disabledTimePickerKeys.concat(['p', 'a', 'm', ':', '-']);

            // Choose the allowed keys for the user to be able to type based on whether the time picker is enabled.
            const keysToCheck: string[] = this.enableTimePicker ? enabledTimePickerKeys : disabledTimePickerKeys;
            if (isNaN(Number(key)) && keysToCheck.indexOf(key) === -1) {
                $event.preventDefault();
            }
        }
    }

    /**
     * Clear the date
     */
    public clearDate(): void {
        this.datepicker.clear();
    }

    /**
     * Validate we have a valid date and format
     */
    public validate(control: FormControl): { [s: string]: boolean } {
        this.formControl = control;

        if (!this.datePickerInitialized) {
            this.datePickerInitialized = true;
            this.initDatePicker();
        }

        if (!this.isValidDate()) {
            this.date = null;
            return { invalid: true };
        }

        return null;
    }

    /**
     * set the date and convert it to the specified timezone if indicated
     */
    private setDate(date: Date, convertTimeZone: boolean): void {
        if (isNaN(date as any)) {
            date = null;
        }

        if (date && this.timeZone && convertTimeZone) {
            // Convert the date to the local time from the timezone time
            const localTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
            date = utcToZonedTime(zonedTimeToUtc(date, localTimezone), this.timeZone);
        }

        if (this.datepicker &&
            (!this.isDateCompareEqual(this.selectedDate, date) ||
                (!this.el.nativeElement.value && date))) {
            // the date changed so select the date, which will fire the
            // dateChanged event.
            this.datepicker.selectDate(date);
        }

        this._date = date;
    }

    /**
     * Compare two dates and return if they are equal or not
     */
    private isDateCompareEqual(date1: Date, date2: Date): boolean {
        if (!date1 && !date2) {
            return true;
        }

        if (date1 && date2 &&
            date1.getTime() === date2.getTime()) {
            return true;
        }

        return false;
    }

    private initDatePicker(): void {
        const dateCtrl: any = $(this.el.nativeElement);

        dateCtrl.datepicker({
            language: 'en',
            startDate: null,
            keyboardNav: false,
            timepicker: this.enableTimePicker,
            onlyTimepicker: this.onlyTimePicker,
            minutesStep: this.minuteStep,
            dateFormat: this.dateFormat,
            timeFormat: this.timeFormat,
            position: this.position,
            onSelect: (formattedDate: string, date: Date): void => {
                this.datepicker.view = 'days';
                date = date || null;

                // A date was selected or entered so fire
                // the dateChanged event
                this.selectedDate = date;

                // The date is currently set to the timeZone's offset minutes so we need to convert it
                // it back to the computer's timezone for storing with the object.
                if (date && this.timeZone) {
                    const localTimezone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    const updatedDate: Date = utcToZonedTime(zonedTimeToUtc(date, this.timeZone), localTimezone);
                    this.dateChange.emit(updatedDate);
                } else {
                    this.dateChange.emit(date);
                }

                // We need need to set the ngModel on the control so the required validation
                // will work properly. Here we will update the value to the same as the user is seeing
                if (this.formControl) {
                    this.formControl.setValue(formattedDate, { onlySelf: true });
                    this.formControl.updateValueAndValidity();
                }
            },
        });

        this.datepicker = dateCtrl.data('datepicker');
        this.datepicker.selectDate(this.date);

        dateCtrl.on('focusout', () => {
            let text: string = this.el.nativeElement.value;

            if (text && this.onlyTimePicker) {
                this.setTimeOfDay(this.date, text);
                text = this.date.toISOString();
            }

            if (text && this.isValidDate()) {
                this.setDate(new Date(text), false);
                return;
            }

            this.date = null;
        });
    }

    /**
     * Determines if the current date is valid
     */
    private isValidDate(): boolean {
        let value: string = <string> this.el.nativeElement.value.toLowerCase();

        if (value &&
            !this.enableTimePicker &&
            !(new RegExp(this.validDateRegex)).test(value)) {
            return false;
        }

        if (value &&
            this.enableTimePicker &&
            (value.indexOf(':') === -1 || (value.indexOf('am') === -1 && value.indexOf('pm') === -1))) {
            return false;
        }

        if (value && this.onlyTimePicker) {
            this.setTimeOfDay(this.date, value);
            value = this.date.toISOString();
        }

        if (value && !isValid(new Date(value))) {
            return false;
        }

        if (this.datepicker.date && !isValid(this.datepicker.date.getDate())) {
            return false;
        }

        return true;
    }

    /**
     * Parses the `time` string and sets it on the `date` object.
     * @example Date: "2023-05-18"
     * - time: 05:32am >> 2023-05-17T10:32:00.000Z
     * - time: 8:15pm >> 2023-05-18T01:15:00.000Z
     * - time: 15:50 >> 2023-05-17T20:50:00.000Z
     */
    private setTimeOfDay(date: Date, time: string): void {
        if (!date || !time) return;

        const ampm: string = time.includes('m') ? time.substring(time.indexOf('m') - 1) : 'am';
        const [hours, minutes]: number[] = time
            .replace(ampm, '')
            .split(':').map((n, i) => Number(n) + (i === 0 && ampm === 'pm' ? 12 : 0));
        date.setHours(hours, minutes, 0);
    }
}
